import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { cn } from '@/lib/utils';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { RadioGroup, RadioGroupOption } from '@/components/ui/radio-group';

type RadioOption = {
  value: string;
  label: React.ReactNode;
};

type RadioLayout = 'col' | 'row' | 'wrap' | 'scroll-x';

interface FormRadioGroupFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  description?: React.ReactNode;
  required?: boolean;
  options: RadioOption[];
  /**
   * How the options are laid out:
   * - `col` (default): vertical list, each option full-width.
   * - `row`: equal-width horizontal on md+ (stacks on mobile). Use for 2-3 binary choices.
   * - `wrap`: options sized to content and wrapped. Use for many short options.
   */
  layout?: RadioLayout;
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (value: string, fieldOnChange: (...event: any[]) => void) => void;
}

const LAYOUT_CLASS: Record<RadioLayout, string> = {
  col: 'flex flex-col gap-2',
  row: 'flex flex-col gap-3 md:flex-row md:gap-4',
  wrap: 'flex flex-wrap gap-2 md:gap-3',
  'scroll-x':
    'grid w-full max-w-full grid-flow-col auto-cols-max overflow-x-auto gap-2 pb-2 md:flex md:flex-wrap md:overflow-visible md:pb-0 md:whitespace-normal',
};

export const FormRadioGroupField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  options,
  layout = 'col',
  className,
  onChange,
}: FormRadioGroupFieldProps<TFieldValues>) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className={cn('min-w-0 w-full max-w-full block', className)}>
        <FormLabel>
          {label} {required && <span className="text-primary">*</span>}
        </FormLabel>
        <FormControl>
          <RadioGroup
            onValueChange={(val) => {
              if (onChange) {
                onChange(val, field.onChange);
              } else {
                field.onChange(val);
              }
            }}
            value={field.value}
            className={LAYOUT_CLASS[layout]}
          >
            {options.map((option) => (
              <RadioGroupOption
                key={option.value}
                value={option.value}
                label={option.label}
                className={cn(layout === 'row' && 'md:flex-1')}
              />
            ))}
          </RadioGroup>
        </FormControl>
        {description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
      </FormItem>
    )}
  />
);
FormRadioGroupField.displayName = 'FormRadioGroupField';
